"use client";

import { useState } from "react";
import { CopyButton } from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  HINT,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import UnitPlanDocument from "@/components/tools/games-organizers/unit-plan-document";
import UnitPlanForm from "@/components/tools/games-organizers/unit-plan-form";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { downloadText } from "@/lib/tool-kit/download";
import {
  buildPlan,
  EMPTY_INPUTS,
  type PlanLabels,
  planSections,
  planToHtml,
  planToText,
  type UnitInputs,
} from "@/lib/tool-kit/games-organizers/unit-plan";

/** English strings. A translated content file overrides these through `ui`. */
export const UNIT_PLAN_GENERATOR_STRINGS = {
  basicsHeading: "The unit",
  titleLabel: "Unit title",
  titlePlaceholder: "Energy and Matter in Ecosystems",
  subjectLabel: "Subject",
  subjectPlaceholder: "Science",
  gradeLabel: "Grade or year",
  gradePlaceholder: "Grade 7",
  weeksLabel: "Length of the unit",
  weeksOption: "one:{count} week|other:{count} weeks",
  perWeekLabel: "Lessons per week",
  minutesLabel: "Lesson length",
  minutesOption: "{count} minutes",
  stage1: "Stage 1 — Desired results",
  stage1Hint:
    "Begin with the end: what should students understand, know and be able to do when the unit is over?",
  stage2: "Stage 2 — Assessment evidence",
  stage2Hint:
    "What will students do or produce to show they have reached the results?",
  stage3: "Stage 3 — Learning plan",
  stage3Hint:
    "List one lesson per line, in order. They are spread across the weeks and each is placed in the arc of the unit.",
  listHint: "One per line.",
  lessonsHint:
    "Lessons left blank stay as numbered placeholders. Extra lines beyond the unit's lessons are ignored.",
  fields: {
    goals: "Established goals or standards",
    understandings: "Understandings (the big ideas)",
    questions: "Essential questions",
    knowledge: "Students will know…",
    skills: "Students will be able to…",
    taskTitle: "Performance task: title",
    taskDescription: "Performance task: what students do",
    evidence:
      "Other evidence (quizzes, journals, observations, self-assessment)",
    lessons: "Lesson sequence",
  },
  taskHeading: "Performance task",
  loadExample: "Load example unit",
  clearAll: "Clear all fields",
  heading: "Your unit plan",
  copy: "Copy plan",
  copied: "Copied!",
  download: "Download as text file",
  fileName: "unit-plan.md",
  untitled: "Untitled unit",
  weeksCount: "one:{count} week|other:{count} weeks",
  lessonsCount: "one:{count} lesson|other:{count} lessons",
  lessonLength: "{lessons} of {minutes} minutes",
  totalTime: "about {hours} hours of lesson time",
  extraTitles:
    "{count} lesson lines were left out because the unit has fewer lessons. Add weeks or lessons per week to use them.",
  toComplete: "(to be completed)",
  weekHeading: "Week {week}",
  tableHead: ["No.", "Lesson focus", "Stage of the unit", "WHERETO elements"],
  untitledLesson: "(lesson to be planned)",
  checklistHeading: "Completeness check",
  checks: {
    goals: "Established goals listed",
    understandings: "Understandings written",
    questions: "Essential questions written",
    knowledge: "Knowledge listed",
    skills: "Skills listed",
    task: "Performance task described",
    evidence: "Other evidence listed",
    lessons: "Lessons titled",
  },
  done: "Done",
  todo: "To do",
  phases: {
    launch: "Launch",
    build: "Build",
    apply: "Apply",
    checkin: "Check-in",
    revise: "Revise",
    assess: "Assess",
  },
  elements: {
    where: "Where, why, what",
    hook: "Hook",
    equip: "Equip",
    rethink: "Rethink",
    evaluate: "Evaluate",
    tailor: "Tailor",
    organize: "Organize",
  },
  exampleUnit: {
    title: "Energy and Matter in Ecosystems",
    subject: "Science",
    grade: "Grade 7",
    goals:
      "Describe how energy flows and matter cycles through an ecosystem\nUse evidence to explain how a change to one population affects others",
    understandings:
      "Energy enters most ecosystems as sunlight and moves through food webs, while matter is recycled.\nOrganisms depend on one another, so a change to one population ripples through the rest.",
    questions:
      "What happens to the energy and matter in a meal?\nHow can removing one species change a whole ecosystem?",
    knowledge:
      "Producers, consumers and decomposers\nFood chains, food webs and energy pyramids\nPhotosynthesis and respiration as inputs and outputs of matter",
    skills:
      "Construct and interpret a food web\nUse data to support a claim about an ecosystem change\nPresent a scientific argument",
    taskTitle: "Ecosystem in Balance: a briefing for the park service",
    taskDescription:
      "Students choose a local ecosystem, model its food web, predict the effect of removing one species, and present the case as a short briefing with evidence.",
    evidence:
      "Weekly exit tickets on key terms\nAnnotated food web diagram\nUnit test: data interpretation and short answers\nSelf-assessment against the briefing rubric",
    lessons:
      "Hook: a day in the life of a pond\nProducers, consumers and decomposers\nFood chains from our schoolyard\nBuilding a food web\nEnergy pyramids: where does the energy go?\nPhotosynthesis and respiration\nMatter cycles: carbon and decomposers\nPopulation change: reading graphs\nMid-unit check: concept map and quiz\nPredator and prey case study\nKeystone and invasive species\nPlanning the briefing: claim, evidence, reasoning\nWorkshop: building the ecosystem model\nWorkshop: gathering evidence\nPeer feedback and revision\nBriefings and unit reflection",
  },
} as const;

const EXAMPLE: UnitInputs = {
  ...EMPTY_INPUTS,
  ...UNIT_PLAN_GENERATOR_STRINGS.exampleUnit,
  weeks: 4,
  perWeek: 4,
  minutes: 45,
};

export default function UnitPlanGeneratorWidget() {
  const strings = useToolUi(UNIT_PLAN_GENERATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [inputs, setInputs] = useState<UnitInputs>(EMPTY_INPUTS);

  const plan = buildPlan(inputs);
  const joined = [inputs.subject.trim(), inputs.grade.trim()]
    .filter((part) => part !== "")
    .join(" · ");
  const summary = [
    joined,
    tPlural(strings.weeksCount, locale, plan.weeks, { count: fmt(plan.weeks) }),
    t(strings.lessonLength, {
      lessons: tPlural(strings.lessonsCount, locale, plan.total, {
        count: fmt(plan.total),
      }),
      minutes: fmt(plan.minutes),
    }),
    t(strings.totalTime, {
      hours: fmt(plan.totalMinutes / 60, { maximumFractionDigits: 1 }),
    }),
  ]
    .filter((part) => part !== "")
    .join(" · ");
  const labels: PlanLabels = {
    summary,
    stage1: strings.stage1,
    stage2: strings.stage2,
    stage3: strings.stage3,
    goals: strings.fields.goals,
    understandings: strings.fields.understandings,
    questions: strings.fields.questions,
    knowledge: strings.fields.knowledge,
    skills: strings.fields.skills,
    task: strings.taskHeading,
    evidence: strings.fields.evidence,
    toComplete: strings.toComplete,
    weekHeading: strings.weekHeading,
    tableHead: [
      strings.tableHead[0],
      strings.tableHead[1],
      strings.tableHead[2],
      strings.tableHead[3],
    ],
    untitled: strings.untitled,
    phases: strings.phases,
    elements: strings.elements,
  };
  const sections = planSections(inputs, plan, labels, (n) => fmt(n));
  const markdown = planToText(sections);

  return (
    <div className="space-y-8">
      <div className="space-y-4">
        <div className="flex flex-wrap gap-3">
          <button
            className={BUTTON_SMALL}
            onClick={() => setInputs(EXAMPLE)}
            type="button"
          >
            {strings.loadExample}
          </button>
          <button
            className={BUTTON_SMALL}
            onClick={() => setInputs(EMPTY_INPUTS)}
            type="button"
          >
            {strings.clearAll}
          </button>
        </div>
        <UnitPlanForm
          inputs={inputs}
          onChange={(patch) =>
            setInputs((current) => ({ ...current, ...patch }))
          }
          strings={strings}
        />
      </div>

      <div aria-live="polite" className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className={WIDGET_H2}>{strings.heading}</h2>
          <div className="flex flex-wrap gap-2">
            <CopyButton
              copiedLabel={strings.copied}
              html={planToHtml(sections)}
              label={strings.copy}
              text={markdown}
            />
            <button
              className={BUTTON_SECONDARY}
              onClick={() =>
                downloadText(
                  strings.fileName,
                  markdown,
                  "text/markdown;charset=utf-8"
                )
              }
              type="button"
            >
              {strings.download}
            </button>
          </div>
        </div>
        {plan.extraTitles > 0 ? (
          <p className={HINT}>
            {t(strings.extraTitles, { count: fmt(plan.extraTitles) })}
          </p>
        ) : null}
        <div className="space-y-2">
          <h3 className={WIDGET_H3}>{strings.checklistHeading}</h3>
          <ul className="m-0 grid list-none gap-1 p-0 text-sm sm:grid-cols-2">
            {plan.checks.map((check) => (
              <li className="text-foreground" key={check.key}>
                <span className="font-medium">
                  {check.done ? strings.done : strings.todo}
                </span>{" "}
                — {strings.checks[check.key]}
              </li>
            ))}
          </ul>
        </div>
        <UnitPlanDocument
          placeholder={strings.toComplete}
          sections={sections}
        />
      </div>
    </div>
  );
}
